import React from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function PrivacyPolicy() {
  const { user } = useAuthStore();
  useSEO(
    'Privacy Policy | JepPrep',
    'Learn how JepPrep collects, uses, and protects your personal information. Read our full privacy policy for the trivia training platform.',
    { canonical: 'https://jepprep.com/privacy' }
  );

  const Section = ({ title, children }) => (
    <div className="mb-8">
      <h2 className="text-lg font-bold text-white mb-3">{title}</h2>
      <div className="text-gray-400 text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-10" />
            </div>
            {user && (
              <Link to="/dashboard">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold text-sm" data-testid="privacy-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Privacy Policy</h1>
        </div>
        <p className="text-gray-500 text-sm mb-10">Last updated: April 4, 2026</p>

        <Section title="1. Information We Collect">
          <p><strong className="text-gray-300">Account Information:</strong> When you create an account, we collect your email address, display name, and a hashed version of your password. We never store your password in plain text.</p>
          <p><strong className="text-gray-300">Game Data:</strong> We collect data about your gameplay activity, including scores, session results, streaks, achievements, board creations, and community interactions. This data is used to power features like leaderboards, statistics, and progress tracking.</p>
          <p><strong className="text-gray-300">Usage Data:</strong> We may collect general usage information such as pages visited, features used, and time spent on the platform to improve the Service.</p>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Provide, maintain, and improve the Service</li>
            <li>Create and manage your user account</li>
            <li>Display your performance on leaderboards and community features</li>
            <li>Track your achievements, streaks, and game progress</li>
            <li>Generate personalized trivia content through AI</li>
            <li>Communicate with you about service updates or account issues</li>
            <li>Detect and prevent fraud, abuse, or violations of our Terms of Service</li>
          </ul>
        </Section>

        <Section title="3. Data Sharing">
          <p>We do not sell your personal information to third parties. Your information may be visible to other users in the following contexts:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong className="text-gray-300">Public profiles:</strong> Your display name, avatar, and game statistics may be visible on leaderboards and community board pages</li>
            <li><strong className="text-gray-300">Community boards:</strong> Boards you share to the community are visible to all users along with your display name</li>
          </ul>
          <p>We may share data with third-party services solely for the purpose of operating the platform (e.g., AI content generation through OpenAI). These services are bound by their own privacy policies.</p>
        </Section>

        <Section title="4. Data Security">
          <p>We implement industry-standard security measures to protect your information:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Passwords are hashed using SHA-256 with salt before storage</li>
            <li>Authentication is handled via secure JWT tokens</li>
            <li>All data transmission uses HTTPS encryption</li>
            <li>Database access is restricted and monitored</li>
          </ul>
          <p>While we take reasonable steps to protect your data, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security.</p>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain your account information and game data for as long as your account is active. If you request account deletion, we will permanently remove your personal data and all associated game records from our systems.</p>
        </Section>

        <Section title="6. Cookies and Local Storage">
          <p>JepPrep uses browser local storage to maintain your authentication session and user preferences. We do not use third-party tracking cookies. No advertising cookies or analytics trackers are employed.</p>
        </Section>

        <Section title="7. Third-Party Services">
          <p>The Service integrates with the following third-party providers:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong className="text-gray-300">OpenAI:</strong> Used for AI-powered trivia board generation and clue paraphrasing. Game prompts (not personal data) are sent to OpenAI APIs.</li>
            <li><strong className="text-gray-300">DiceBear:</strong> Used for generating user avatars. No personal data is shared with DiceBear.</li>
          </ul>
        </Section>

        <Section title="8. Children's Privacy">
          <p>The Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13, we will take steps to delete that information promptly.</p>
        </Section>

        <Section title="9. Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and associated data</li>
            <li>Withdraw from community features at any time</li>
          </ul>
          <p>To exercise any of these rights, please contact us through the platform.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date at the top of this page. We encourage you to review this policy periodically. Continued use of the Service after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact">
          <p>If you have questions or concerns about this Privacy Policy, please reach out to us through the platform.</p>
        </Section>
      </main>

      <footer className="py-8 bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]">
        <div className="mx-auto max-w-4xl px-4 flex flex-col items-center gap-3">
          <img src={LOGO_URL} alt="JepPrep" className="h-10" />
          <div className="flex items-center gap-4 text-gray-500 text-sm">
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <span>|</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
          <p className="text-gray-500 text-sm text-center max-w-2xl">
            © 2026 JepPrep. This application is an independent, fan-made trivia training tool and is not affiliated with, 
            endorsed by, or sponsored by Jeopardy!, Sony Pictures Television, or any of their subsidiaries or affiliates. 
            "Jeopardy!" is a registered trademark of Sony Pictures Television. All trademarks belong to their respective owners.
          </p>
        </div>
      </footer>
    </div>
  );
}
